import { createContext, useContext, useEffect, useState } from "react";
import { initializeLDClient } from "@/lib/launchdarkly";
import { useCompany } from "@/hooks/useCompany";
const CLIENT_SIDE_ID = process.env.NEXT_PUBLIC_LAUNCHDARKLY_CLIENT_ID;
type LaunchDarklyContextType = {
  flags: Record<string, boolean>;
  loading: boolean;
};
const LaunchDarklyContext = createContext<LaunchDarklyContextType>({
  flags: {},
  loading: true
});
export function LaunchDarklyProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [flags, setFlags] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const {
    data: company
  } = useCompany();
  useEffect(() => {
    if (!company) return;
    const client = initializeLDClient(CLIENT_SIDE_ID ?? "", {
      email: company.email,
      companyId: company.id.toString()
    });
    if (!client) {
      setLoading(false);
      return;
    }
    client.on("ready", () => {
      const allFlags = client.allFlags();
      setFlags(allFlags);
      setLoading(false);
    });
    client.on("change", (changes: Record<string, {
      current: boolean;
    }>) => {
      setFlags(prevFlags => ({
        ...prevFlags,
        ...Object.fromEntries(Object.entries(changes).map(([key, value]) => [key, value.current]))
      }));
    });
    return () => {
      client.close();
    };
  }, [company]);
  return <LaunchDarklyContext.Provider value={{
    flags,
    loading
  }} data-sentry-element="unknown" data-sentry-component="LaunchDarklyProvider" data-sentry-source-file="launchdarkly-context.tsx">
      {children}
    </LaunchDarklyContext.Provider>;
}
export function useLaunchDarkly() {
  const context = useContext(LaunchDarklyContext);
  if (context === undefined) {
    throw new Error("useLaunchDarkly must be used within a LaunchDarklyProvider");
  }
  return context;
}